import React, { ReactElement, useContext, useEffect, useState } from "react";
import {
  FieldInput,
  FieldsManager,
  FormContext,
  FormFieldProps,
  FormFieldWrapperProps,
  FormInputOption,
  FormInputOptions,
  InputProps,
  LabelProps,
  RadioInputProps,
} from "../lib/form-fields.ts";
import { useResult } from "../hooks/use-result.ts";
import { RequestContext } from "../lib/request-context.ts";
import { PropTypes } from "https://esm.sh/v132/prop-types@15.8.1/denonext/prop-types.mjs";
import { isBoolean } from "../../node_modules/kysely/dist/esm/util/object-utils.js";

export type * from "../lib/form-fields.ts";
export { FormContext, FormInputType } from "../lib/form-fields.ts"; // consumers of these components will often use this

const FormFieldWrapper = ({
  name,
  errors,
  cssClasses,
  children,
}: FormFieldWrapperProps) => {
  const hasErrors = !!(errors && errors.length);
  return (
    <div
      className={`FormField ${cssClasses ? cssClasses : ""} ${hasErrors ? "--has-errors" : ""}`}
    >
      {children}
      {hasErrors
        ? (
          <div
            className="FormField__Error"
            id={`${name}-errors`}
            data-testid={`${name}-errors`}
          >
            <ul>
              {errors.map((error, i) => {
                return <li key={`${name}-error-${i}`}>{error}</li>;
              })}
            </ul>
          </div>
        )
        : null}
    </div>
  );
};

/* for rendering default form inputs - anything that the browser handles well enough by default
i.e., text, email, password, number, etc

there are specialized components for radio inputs and textareas below
*/
export const FormField: React.FC<{
  name: string;
  options?: { [key: string]: string };
}> = (props: FormFieldProps) => {
  const manager = useContext(FormContext);

  const field = manager!.get(props.name);

  const inputs = [];
  const { name } = props as { name: string };
  const { errors } = field;
  //this label + placeholderoverride is used in signin.tsx. If this becomes a pattern, we should consider a more general solution
  let { label, placeholder } = field;
  if (props.options?.label) label = props.options.label;
  if (props.options?.placeholder) placeholder = props.options.placeholder;

  const handleInput = createInputHandler({ name, manager: manager! });
  inputs.push(
    <Label name={name} label={label} key={`${name}-label`} />,
    <Input
      name={name}
      placeholder={placeholder}
      field={field}
      handleInput={handleInput}
      isConfirm={false}
      key={`${name}`}
      defaultChecked={!!field.value}
    />,
  );
  if (field.validation?.confirmed) {
    /* conditionally add a "confirmed" label + input */
    inputs.push(
      <Label
        name={name}
        label={field.validation.confirmed.label || `Confirm ${label}`}
        confirmed={true}
        key={`${name}-label-confirmed`}
      />,
      <Input
        name={name}
        field={field}
        handleInput={handleInput}
        isConfirm={true}
        key={`${name}-confirmed`}
      />,
    );
  }

  return (
    <FormFieldWrapper
      name={name}
      errors={errors}
      cssClasses={`field-type-${field.type}`}
    >
      {inputs}
    </FormFieldWrapper>
  );
};

export const TextAreaFormField: React.FC<{
  name: string;
}> = (props: FormFieldProps) => {
  const manager = useContext(FormContext);

  const field = manager!.get(props.name);

  const inputs = [];
  const { name } = props as { name: string };
  const { label, errors } = field;

  const handleInput = createInputHandler({ name, manager: manager! });
  inputs.push(
    <Label name={name} label={label} key={`${name}-label`} />,
    <TextArea
      name={name}
      field={field}
      handleInput={handleInput}
      isConfirm={false}
      key={`${name}`}
    />,
  );
  if (field.validation?.confirmed) {
    /* conditionally add a "confirmed" label + input */
    inputs.push(
      <Label
        name={name}
        label={field.validation.confirmed.label || `Confirm ${label}`}
        confirmed={true}
        key={`${name}-label-confirmed`}
      />,
      <TextArea
        name={name}
        field={field}
        handleInput={handleInput}
        isConfirm={true}
        key={`${name}-confirmed`}
      />,
    );
  }

  return (
    <FormFieldWrapper name={name} errors={errors}>
      {inputs}
    </FormFieldWrapper>
  );
};

/* callback is used by contribution-options-field to set parent component state after an OptionsFormField value changes */
function createInputHandler(
  props: {
    name: string;
    manager: FieldsManager;
    callback?: () => void;
  },
) {
  return function handleInput(inputVal: string, isConfirm = false) {
    const { name, manager, callback } = props;

    /* set new field value */
    if (isConfirm) {
      manager.confirmValue(name, inputVal);
    } else {
      manager.setValue(name, inputVal);
    }

    /* clear errors if applicable */
    if (manager.hasValidation(name, "confirmed")) {
      if (manager.hasValue(name, inputVal)) {
        manager.clearErrors(name);
      }
    } else {
      manager.clearErrors(name);
    }

    if (callback) callback();
  };
}

function OptionsFormField2(
  props: React.PropsWithChildren<{
    name: string;
    options: FormInputOptions;
    renderOption?: OptionRenderer;
    cssClasses?: string;
    onChangeCallback?: () => void;
    noRadioStyle?: boolean;
  }>,
) {
}

/* for rendering intputs for a FormField of type Radio with options  */
export const OptionsFormField: React.FC<
  {
    name: string;
    options: FormInputOptions;
    renderOption?: OptionRenderer;
    cssClasses?: string;
    onChangeCallback?: () => void;
    noRadioStyle?: boolean;
  } & React.PropsWithChildren
> = (
  { name, options, renderOption, cssClasses, onChangeCallback, noRadioStyle, children },
) => {
  const manager = useContext(FormContext)!;
  const field = manager.get(name);

  const vals = Object.entries(options!).map(([id, info]) => ({ ...info, id }));

  const onInput = createInputHandler({
    name: name,
    manager: manager,
    callback: onChangeCallback,
  });
  if (!vals) {
    throw new Error("need vals");
  }

  const radioItems = vals.map((opt) => {
    const inputValue = opt.id;
    const checked = inputValue === field.value;
    const radioInputId = `${name}-${inputValue}`;

    const input = (
      <RadioInput
        name={name}
        errors={field.errors}
        value={inputValue}
        checked={checked}
        handleInput={onInput}
      />
    );
    //if renderOption is provided, use it to render this label + option input
    if (renderOption) return renderOption(opt, field, input);
    return (
      <Label
        key={`${radioInputId}-label`}
        name={name}
        htmlFor={`${radioInputId}`}
        additionalCSSClasses={`${noRadioStyle ? "" : "--radio"} ${checked ? "--checked" : ""}`}
        isActuallyDiv={noRadioStyle}
      >
        <div className="FormField__FlexContainer">
          <div className="FormField__FakeRadioContainer">
            {input}
            <div className={`FormField__FakeRadio ${checked && "--checked"}`}></div>
          </div>
          {opt.label}
        </div>
      </Label>
    );
  });
  return (
    <FormFieldWrapper name={name} errors={field.errors} cssClasses={cssClasses}>
      {radioItems}
      {children}
    </FormFieldWrapper>
  );
};

export const Label = ({
  name,
  label,
  htmlFor,
  additionalCSSClasses,
  confirmed,
  children,
  isActuallyDiv,
  disableSelect,
}: LabelProps): ReactElement => {
  const forString = htmlFor || `${name} ${confirmed ? "--confirmed" : ""}`;
  if (isActuallyDiv) {
    return (
      <div
        className={`FormField__Label ${additionalCSSClasses || ""}`}
        key={`${name}-label ${confirmed ? "--confirmed" : ""}`}
      >
        {label}
        {children}
      </div>
    );
  }
  return (
    <label
      className={`FormField__Label ${additionalCSSClasses || ""} ${
        disableSelect ? "--disable-select" : ""
      }`}
      key={`${name}-label ${confirmed ? "--confirmed" : ""}`}
      htmlFor={forString}
    >
      {label}
      {children}
    </label>
  );
};

const Input = ({
  name,
  placeholder,
  field,
  handleInput,
  isConfirm,
  defaultChecked,
}: InputProps): ReactElement => {
  const { value, type, errors } = field;
  //here we allow the placeholder to be overwritten by a prop, this is only used in
  //signin.tsx. See the FormField component for more info
  if (!placeholder) {
    placeholder = field.placeholder;
  }
  const hasErrors = !!(errors && errors.length);

  const [inputType, setInputType] = useState(type);

  return (
    <>
      <input
        id={`${name} ${isConfirm ? "--confirmed" : ""}`}
        data-testid={`${name} ${isConfirm ? "--confirmed" : ""}`}
        className={`FormField__Input ${isConfirm ? "--confirmed" : ""}`}
        key={`${name}${isConfirm ? "-confirmed" : ""}`}
        name={`${name}${isConfirm ? "-confirmed" : ""}`}
        type={inputType}
        defaultValue={isConfirm ? "" : value}
        placeholder={placeholder ? placeholder : ""}
        autoComplete={type}
        aria-invalid={hasErrors}
        aria-errormessage={`${name}-errors`}
        defaultChecked={defaultChecked}
        onChange={(evt) => {
          let value = evt.target.value;
          if (evt.target.type === "checkbox") {
            value = evt.target.checked
              .toString();
          }
          handleInput(value, isConfirm);
        }}
      />
      {type === "password" && (
        <div
          className="FormField__TypeToggle"
          onClick={() => {
            setInputType(inputType === "password" ? "text" : "password");
          }}
        >
          <span
            style={inputType === "password"
              ? { opacity: 0, width: 0, position: "absolute", right: "-10000px" }
              : {}}
          >
            <i
              className={`fa-solid fa-eye-slash `}
            >
            </i>
          </span>
          <span
            style={inputType === "password"
              ? {}
              : { opacity: 0, width: 0, position: "absolute", right: "-10000px" }}
          >
            <i className={`fa-solid fa-eye `}>
            </i>
          </span>
        </div>
      )}
      {type === "checkbox" && (
        <div className="FormField__FakeCheckbox">
          <span className="checkmark">
            <i className="fa-solid fa-check"></i>
          </span>
        </div>
      )}
    </>
  );
};

const TextArea = ({
  name,
  field,
  handleInput,
  isConfirm,
}: InputProps): ReactElement => {
  const { value, type, placeholder, errors } = field;
  const hasErrors = !!(errors && errors.length);

  return (
    <textarea
      id={`${name} ${isConfirm ? "--confirmed" : ""}`}
      data-testid={`${name} ${isConfirm ? "--confirmed" : ""}`}
      className={`FormField__Input ${isConfirm ? "--confirmed" : ""}`}
      key={`${name}${isConfirm ? "-confirmed" : ""}`}
      name={`${name}${isConfirm ? "-confirmed" : ""}`}
      placeholder={placeholder ? placeholder : ""}
      autoComplete={type}
      aria-invalid={hasErrors}
      aria-errormessage={`${name}-errors`}
      onChange={(evt) => {
        const value = evt.target.value;
        handleInput(value, isConfirm);
      }}
      maxLength={1500}
    >
      {isConfirm ? "" : value}
    </textarea>
  );
};

const RadioInput = ({
  name,
  errors,
  value,
  checked,
  handleInput,
}: RadioInputProps): ReactElement => {
  const hasErrors = !!(errors && errors.length);

  return (
    <input
      name={name}
      type="radio"
      id={`${name}-${value}`}
      data-testid={`${name}-${value}`}
      aria-invalid={hasErrors}
      aria-errormessage={`${name}-errors`}
      checked={checked}
      autoComplete="off"
      value={value}
      onChange={() => {
        handleInput(value);
      }}
    />
  );
};

export type OptionRenderer = (
  opt: FormInputOption & { id: string },
  field: FieldInput,
  input: React.ReactElement<typeof RadioInput>,
) => React.ReactElement | React.ReactNode;

/*
  GenericErrors looks for errors on the errors object from useResult.
  Its not really a form field, its a way to display errors from the server
  that may not be associated with a specific field param
*/
export const GenericErrors: React.FC = () => {
  const { confirmation, errors } = useResult();
  if (errors && errors.length > 0) {
    return errors.map((error, i) => {
      return (
        <div className={`GenericErrors --${error.type}`} key={i}>
          <ul>
            <li>{error.message}</li>
          </ul>
        </div>
      );
    });
  }
};
